import dayjs from 'dayjs';
import { getPlacementsChartReport } from 'lib/api/reports';
import { ChartData } from 'lib/api/reports/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import { loggerDev } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import DailyAnalysis from './DailyAnalysis';
import { dataFilters } from './LineChart';
import MainGraph from './MainGraph';

const Placements: React.FC = () => {
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [businessesNames, setBusinessesNames] = useState<string[]>([]);
  const [stakeholdersNames, setStakeholdersNames] = useState<string[]>([]);
  const [executivesNames, setExecutivesNames] = useState<string[]>([]);
  const [creditLines, setCreditLines] = useState<Record<string, number>>({});
  const [chartDataFiltered, setChartDataFiltered] = useState<ChartData[]>([]);
  const startDateDefault = new Date();
  startDateDefault.setDate(1);
  const endDateDefault = new Date();
  const [startDate, setStartDate] = useState<string>(
    startDateDefault.toISOString().split('T')[0],
  );
  const [endDate, setEndDate] = useState<string>(
    endDateDefault.toISOString().split('T')[0],
  );
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const handleSelectChartDataFilter = ({
    selectedFilters,
    newChartData,
  }: {
    selectedFilters: string[];
    newChartData?: ChartData[];
  }) => {
    const dataToFilter = newChartData || chartData;
    const selectedFiltersToUse = selectedFilters || dataFilters;
    const filters = [...selectedFiltersToUse, 'name'];
    setChartDataFiltered(
      dataToFilter.map((obj: ChartData) => {
        return Object.fromEntries(
          Object.entries(obj).filter(([key]) => filters.includes(key)),
        );
      }) as ChartData[],
    );
  };

  const buildQuery = () => {
    let query = '';
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}`;
    return query;
  };

  const fetchPlacementsChartReport = async () => {
    try {
      setLoading(true);
      const query = buildQuery();
      const response = await getPlacementsChartReport(dispatch, query);
      if (response) {
        setChartData(response.mainOutput);
        setBusinessesNames(response.businessesNames);
        setStakeholdersNames(response.stakeholdersNames);
        setExecutivesNames(response.executivesNames);
        setCreditLines(response.creditLines);
      }
    } catch (error) {
      loggerDev(error);
    } finally {
      setLoading(false);
    }
  };

  const triggerSearch = async () => {
    setLoading(true);
    await fetchPlacementsChartReport();
    setLoading(false);
  };

  const filters: Filter[] = [
    {
      label: 'Fecha inicio',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha fin',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
  ];

  useEffect(() => {
    void fetchPlacementsChartReport();
  }, []);

  const buttons: CustomButtonProps[] = [
    {
      name: 'Filtro',
      action: () => {},
      color: 'primary',
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={triggerSearch}
          dispatch={dispatch}
          disableSearch={loading}
          maxMonths={3}
        />
      ),
    },
  ];

  if (loading) {
    return <CenteredCircularProgress />;
  }

  return (
    <>
      <HeaderWithActions
        customTitle={`Informe de colocaciones ${startDate ? dayjs(startDate).format('DD/MM/YYYY') : ''} - ${endDate ? dayjs(endDate).format('DD/MM/YYYY') : ''}`}
        variant="body1"
        buttons={buttons}
      />
      <MainGraph
        handleSelectChartDataFilter={handleSelectChartDataFilter}
        chartDataFiltered={chartDataFiltered}
        chartData={chartData}
        businessesNames={businessesNames}
        stakeholdersNames={stakeholdersNames}
        executivesNames={executivesNames}
        setBusinessesNames={setBusinessesNames}
        setStakeholdersNames={setStakeholdersNames}
        setExecutivesNames={setExecutivesNames}
      />

      {/* Análisis Diario */}
      <DailyAnalysis
        data={chartData}
        businessesNames={businessesNames}
        stakeholdersNames={stakeholdersNames}
        executivesNames={executivesNames}
        creditLines={creditLines}
      />
    </>
  );
};

export default Placements;
